// 必要なMaterial UIコンポーネントやアイコン、React hooks、Recoil、axiosライブラリをインポート
import { Box, Button, Container, FilledInput, FormControl, Grid, IconButton, InputAdornment, InputLabel } from "@mui/material";
import { useNavigate } from "react-router-dom"; // ページ遷移用のフック
import { useEffect, useState } from "react"; // React hooks
import { useRecoilState } from "recoil"; // Recoil状態管理用フック
import userState from "../../recoil/atoms/userState"; // ユーザーデータのRecoil状態
import axios from "axios"; // HTTPリクエスト用ライブラリ
import { Visibility, VisibilityOff } from "@mui/icons-material"; // パスワード表示/非表示切り替え用アイコン

// ゲストユーザー登録用テンプレートコンポーネント
const GuestRegisterTemplate = () => {
    const navigate = useNavigate(); // ページ遷移用関数
    // 各入力値を管理するState
    const [name, setName] = useState<string>(""); // 入力された名前
    const [uid, setUid] = useState<string>(""); // 入力されたメールアドレス
    const [password1, setPassword1] = useState<string>(""); // 入力されたパスワード1
    const [password2, setPassword2] = useState<string>(""); // 入力されたパスワード2

    const [userData, setUserData] = useRecoilState(userState); // Recoilのユーザーデータ状態

    // ユーザー登録ボタン押下時の処理
    const onRegister = async () => {
        // パスワード一致の確認
        if (password1 !== password2) {
            alert("パスワードは同じものを入力してください。");
            return;
        }
        // パスワードの形式チェック（8文字以上の半角英数記号）
        const regex = new RegExp(`^[ -~]+$`, "u");
        if (!regex.test(password1) || password1.length < 8) {
            alert("パスワードは８文字以上の半角英数記号で入力してください");
            return;
        }
        // 未入力項目の確認
        if (name === "" || uid === "" || password1 === "" || password2 === "") {
            alert("未入力の項目があります。");
            return;
        }
        // メールアドレス形式の確認
        if (!uid.match(/[a-zA-Z0-9]+[a-zA-Z0-9\._-]*@[a-zA-Z0-9_-]+[a-zA-Z0-9\._-]+/)) {
            alert("メールアドレスが正しいかご確認ください。");
            return;
        }
        // 登録確認ダイアログ表示
        const msg = `下記の内容で登録しますか？\n\nお名前：${name}\ne-mail：${uid}`;
        if (!window.confirm(msg)) {
            return;
        }

        // APIを呼び出してユーザー登録を行う
        await axios.post(process.env.REACT_APP_BE_HOST + 'auth/registerGuest', {
            userName: name,
            password: password1,
            userType: 1, // ユーザータイプ（1:ゲストユーザー）
            companyId: 0,
            failedCnt: 0, // 初期失敗回数
            mailAddress: uid,
            delFlg: 0, // 削除フラグ
        }, {
            withCredentials: true // Cookie情報を含むリクエスト設定
        })
        .then(() => {
            alert("ゲストユーザーとして登録しました。");
            navigate("/"); // 登録成功後にトップページへ遷移
        })
        .catch(() => {
            // 登録失敗時の処理
            const msg = '登録に失敗しました。\n他のメールアドレスをお試しください。';
            alert(msg);
        });
    };

    // 総登録者数の表示
    const [totalUsers, SetTotalUsers] = useState(0); // 登録者数のState
    const getTotalUsers = async () => {
        // APIを呼び出して登録者数を取得
        await axios.get(process.env.REACT_APP_BE_HOST + 'auth/usersCount', {
            withCredentials: true // Cookie情報を含むリクエスト設定
        })
        .then((res) => {
            SetTotalUsers(res.data?.userCount); // APIレスポンスの登録者数を設定
        })
        .catch(() => {});
    };
    useEffect(() => {
        getTotalUsers(); // コンポーネント初回レンダリング時に登録者数を取得
    }, []);

    // パスワードの表示/非表示制御
    const [showPassword, setShowPassword] = useState(false); // パスワード表示状態
    const handleClickShowPassword = () => setShowPassword((show) => !show); // 切り替え処理
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault(); // デフォルト動作を防ぐ
    };
    const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault(); // デフォルト動作を防ぐ
    };

    // レンダリングするUI
    return (
        <>
        <Container maxWidth="xs">
          <Box
            sx={{
                '& .MuiTextField-root': { m: 2, width: '36ch' }, // スタイル設定
            }}
          >
            <Box height={40} />
            <Grid container 
                alignItems='center' 
                justifyContent='center'
                spacing={0}
            >
                <Grid xs={12} sx={{textAlign: 'center'}}>
                    <h4>ゲストユーザー登録</h4>
                    {/* 登録者数の表示 */}
                    {`あなたは${(totalUsers + 1).toLocaleString()}番目の登録者です。`}
                </Grid>
                {/* ユーザー名入力 */}
                <Grid xs={12} sx={{textAlign: 'center'}}>
                    <Box marginBottom={'1rem'}>
                        <FormControl variant="filled">
                            <InputLabel htmlFor="user_name">お名前 *</InputLabel>
                            <FilledInput
                                sx={{width: '20rem'}}
                                required
                                id="user_name"
                                size="small"
                                inputProps={{maxLength: 20}} // 最大文字数
                                onChange={e => {setName(e.target.value)}}
                                value={name}
                            />
                        </FormControl>
                    </Box>
                    {/* メールアドレス入力 */}
                    <Box marginBottom={'1rem'}>
                        <FormControl variant="filled">
                            <InputLabel htmlFor="user_id">メールアドレス *</InputLabel>
                            <FilledInput
                                sx={{width: '20rem'}}
                                required
                                id="user_id"
                                size="small"
                                inputProps={{maxLength: 100}}
                                onChange={e => setUid(e.target.value)}
                                value={uid}
                            />
                        </FormControl>
                    </Box>
                    {/* パスワード入力 */}
                    <Box marginBottom={'1rem'}>
                        <FormControl variant="filled">
                            <InputLabel htmlFor="password1">パスワード *</InputLabel>
                            <FilledInput
                                sx={{
                                    width: '20rem',
                                    "& input[type=password]::-ms-reveal": { display: "none", width: 0, height: 0 },
                                    "& input[type=password]::-ms-clear": { display: "none", width: 0, height: 0 },
                                }}
                                required
                                id="password1"
                                type={showPassword ? 'text' : 'password'} // 表示/非表示切り替え
                                autoComplete="current-password"
                                size="small"
                                inputProps={{maxLength: 20}}
                                onChange={e => setPassword1(e.target.value)}
                                value={password1}
                                endAdornment={
                                    <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        onMouseUp={handleMouseUpPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                    </Box>
                    {/* 確認用パスワード入力 */}
                    <Box>
                        <FormControl variant="filled">
                            <InputLabel htmlFor="password2">パスワード（確認用）*</InputLabel>
                            <FilledInput
                                sx={{
                                    width: '20rem',
                                    "& input[type=password]::-ms-reveal": { display: "none", width: 0, height: 0 },
                                    "& input[type=password]::-ms-clear": { display: "none", width: 0, height: 0 },
                                }}
                                required
                                id="password2"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="confirm-password"
                                size="small"
                                inputProps={{maxLength: 20}}
                                onChange={e => setPassword2(e.target.value)}
                                value={password2}
                                endAdornment={
                                    <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        onMouseUp={handleMouseUpPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                    </Box>
                </Grid>

                {/* 登録ボタン */}
                <Grid xs={12} sx={{textAlign: 'center'}}>
                    <Box marginTop={'2rem'}>
                        <Button 
                            variant="contained"
                            size="large"
                            onClick={() => onRegister()} // 登録処理呼び出し
                        >
                            ゲストユーザーとして登録する
                        </Button>
                    </Box>
                </Grid>  
                {/* 戻るボタン */}
                <Grid xs={12} sx={{textAlign: 'center'}}>
                    <Box marginTop={'1rem'}>
                        <Button 
                            variant="outlined"
                            onClick={() => navigate("/")} // トップページに戻る
                        >
                            戻る
                        </Button>
                    </Box>
                </Grid>              
            </Grid>
          </Box>
        </Container>
        </>
    );
};

export default GuestRegisterTemplate; // コンポーネントのエクスポート
